// The $20 Challenge build draft, ported from the app's
// lib/Games/general/quick/twenty_challenge/auction_engine.dart.
//
// One engine runs one theme (a list of items). Players share a budget of
// whole dollars for the whole theme. The bidding happens out loud; the game
// only records who bought the current item and for how much, then moves on.

export interface WonItem {
  /// Index into the engine's item list (the shuffled game order).
  itemIndex: number;
  name: string;
  price: number;
}

export const MIN_PRICE = 1;

export class AuctionEngine {
  readonly wallets: number[];
  readonly builds: WonItem[][];
  currentItemIndex = 0;
  price = MIN_PRICE;

  constructor(
    readonly playerCount: number,
    readonly budget: number,
    readonly items: string[],
    /// Who opens the bidding for the current item; alternates every item.
    public opener: number,
  ) {
    if (playerCount < 2 || budget < 1 || items.length === 0) throw new Error('bad engine args');
    this.wallets = new Array<number>(playerCount).fill(budget);
    this.builds = Array.from({ length: playerCount }, () => []);
  }

  get isComplete(): boolean {
    return this.currentItemIndex >= this.items.length;
  }

  get currentItem(): string {
    return this.items[this.currentItemIndex];
  }

  get itemCount(): number {
    return this.items.length;
  }

  /// The slider's upper end: one dollar above what the poorer player can
  /// still pay (enough to outbid them), never more than anyone actually has.
  get maxPrice(): number {
    const lowest = Math.min(...this.wallets);
    const highest = Math.max(...this.wallets);
    return Math.max(MIN_PRICE, Math.min(lowest + 1, highest));
  }

  canBuy(player: number): boolean {
    return !this.isComplete && this.wallets[player] >= this.price;
  }

  get anyoneSolvent(): boolean {
    return this.wallets.some((w) => w >= MIN_PRICE);
  }

  setPrice(value: number): void {
    this.price = Math.min(Math.max(Math.round(value), MIN_PRICE), this.maxPrice);
  }

  /// [player] takes the current item for the current price. False (and
  /// nothing happens) if they cannot pay.
  buy(player: number): boolean {
    if (!this.canBuy(player)) return false;
    this.wallets[player] -= this.price;
    this.builds[player].push({ itemIndex: this.currentItemIndex, name: this.currentItem, price: this.price });
    this.advance();
    return true;
  }

  /// Nobody could afford the item: it is dropped and the game moves on.
  skip(): void {
    if (this.isComplete) return;
    this.advance();
  }

  /// Drops items nobody can afford any more. True when the auction is over.
  settle(): boolean {
    while (!this.isComplete && !this.anyoneSolvent) this.skip();
    return this.isComplete;
  }

  private advance(): void {
    this.currentItemIndex++;
    this.opener = (this.opener + 1) % this.playerCount;
    this.price = MIN_PRICE;
  }

  biggestPurchase(): WonItem | null {
    let best: WonItem | null = null;
    for (const b of this.builds) for (const w of b) if (best === null || w.price > best.price) best = w;
    return best;
  }

  /// A plain copy, so React state can hold snapshots for undo.
  clone(): AuctionEngine {
    const e = new AuctionEngine(this.playerCount, this.budget, this.items, this.opener);
    e.wallets.splice(0, e.wallets.length, ...this.wallets);
    e.builds.splice(0, e.builds.length, ...this.builds.map((b) => [...b]));
    e.currentItemIndex = this.currentItemIndex;
    e.price = this.price;
    return e;
  }
}
